import { yupResolver } from '@hookform/resolvers/yup';
import { FormActions } from '@openx/components-core/lib/FormActions/FormActions.tsx';
import { FormContainer } from '@openx/components-core/lib/FormContainer/FormContainer.tsx';
import { scrollToError } from '@openx/components-core/utils/scrollToError.ts';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useProviderContext } from '../../utils/context/ProviderContext.tsx';
import { initialFormState } from '../constants.ts';
import type { ProviderFormState } from '../types.ts';
import { ProviderDetailsSections } from './sections/ProviderDetailsSections.tsx';
import { transformCreateFormDataToApiPayload } from './utils.ts';
import { getValidationSchema } from './validationSchema.ts';

export const ProviderCreateForm = () => {
  const { useCreateProvider } = useProviderContext();
  const { createProvider, loading } = useCreateProvider();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const formMethods = useForm<ProviderFormState>({
    defaultValues: initialFormState,
    mode: 'all',
    resolver: yupResolver(getValidationSchema(t)),
  });

  const onSubmit = (data: ProviderFormState) => {
    const payload = transformCreateFormDataToApiPayload(data);
    createProvider(payload);
  };

  return (
    <PageHeader title={t('Create New Provider')} titlePrefix="OpenXSelect">
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit, scrollToError)}>
          <FormContainer fullPage>
            <ProviderDetailsSections />

            <FormActions
              submitButtonLabel={t('Save')}
              cancelButtonLabel={t('Cancel')}
              onCancel={() => navigate('/providers')}
              isSubmitting={loading}
            />
          </FormContainer>
        </form>
      </FormProvider>
    </PageHeader>
  );
};
